import { useEffect, useRef, type CSSProperties } from "react";

import { useDrawLockStore } from "./drawLock";
import { getLastPointerPoint } from "./pointerTracker";
import {
  INK_ATTR,
  SHIELD_ATTR,
  drawRegionAt,
  drawRegionAtPoint,
} from "./shield";
import { DEFAULT_INK, DOT_SCALE, DOT_SIZE, MORPH_MS } from "./cursorDot";
import "./drawCursor.css";

/**
 * The cursor over every marked draw region (see shield.ts): a dot in the
 * region's ink, a pinpoint while inking (lock held or latched, or button
 * down), hidden elsewhere. Written straight to the DOM per pointer event.
 * Marks can change under a still pointer, so the lock store and an
 * attribute observer both re-read the region at the last known point.
 */
export function DrawCursor() {
  const hostRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    let region: HTMLElement | null = null;
    let pressed = false;

    // Last values written, so a move that changes nothing writes nothing.
    let shownState = host.dataset.state;
    let shownInking = host.dataset.inking;
    let shownColor = host.style.color;

    const paint = () => {
      const locked = useDrawLockStore.getState().mode !== "off";
      const state = region ? "on" : "off";
      const inking = locked || pressed ? "true" : "false";
      if (state !== shownState) host.dataset.state = shownState = state;
      if (inking !== shownInking) host.dataset.inking = shownInking = inking;
      if (region) {
        const color = region.getAttribute(INK_ATTR) ?? DEFAULT_INK;
        if (color !== shownColor) host.style.color = shownColor = color;
      }
      watchRemoval(region !== null);
    };

    const place = (x: number, y: number) => {
      host.style.transform = `translate3d(${Math.round(x)}px, ${Math.round(y)}px, 0)`;
    };

    // Touch is ignored as in pointerTracker: its moves stop at lift and would
    // strand the dot. While a button is down the surface has captured the
    // pointer, so every move names the canvas as target; the region is read
    // once on press, or a drag over the toolbar would keep the dot there.
    const onMove = (e: PointerEvent) => {
      if (e.pointerType === "touch") return;
      if (!pressed) region = drawRegionAt(e.target);
      place(e.clientX, e.clientY);
      paint();
    };
    const onDown = (e: PointerEvent) => {
      if (e.pointerType === "touch") return;
      region = drawRegionAt(e.target);
      pressed = true;
      place(e.clientX, e.clientY);
      paint();
    };
    // A stylus without hover sends nothing after lift; hide the dot. One
    // with hover brings it back on its next move.
    const onUp = (e: PointerEvent) => {
      if (e.pointerType === "touch") return;
      pressed = false;
      if (e.pointerType === "pen") region = null;
      paint();
    };
    const onBlur = () => {
      pressed = false;
      paint();
    };

    const resample = () => {
      const point = getLastPointerPoint();
      if (point) {
        region = drawRegionAtPoint(point.x, point.y);
        place(point.x, point.y);
      }
      paint();
    };

    // A surface that unmounts takes its mark along with no attribute change.
    // Removals are watched only while a region is held: they are frequent,
    // and the re-read forces layout.
    const removals = new MutationObserver(() => {
      if (region && !region.isConnected) resample();
    });
    let watchingRemoval = false;
    const watchRemoval = (on: boolean) => {
      if (on === watchingRemoval) return;
      watchingRemoval = on;
      if (on)
        removals.observe(document.body, { subtree: true, childList: true });
      else removals.disconnect();
    };

    const opts = { capture: true } as const;
    globalThis.addEventListener("pointermove", onMove, opts);
    globalThis.addEventListener("pointerdown", onDown, opts);
    globalThis.addEventListener("pointerup", onUp, opts);
    globalThis.addEventListener("pointercancel", onUp, opts);
    globalThis.addEventListener("blur", onBlur);
    const unsubscribe = useDrawLockStore.subscribe(resample);
    const marks = new MutationObserver(resample);
    marks.observe(document.body, {
      subtree: true,
      attributes: true,
      attributeFilter: [SHIELD_ATTR, INK_ATTR],
    });
    resample();

    return () => {
      globalThis.removeEventListener("pointermove", onMove, opts);
      globalThis.removeEventListener("pointerdown", onDown, opts);
      globalThis.removeEventListener("pointerup", onUp, opts);
      globalThis.removeEventListener("pointercancel", onUp, opts);
      globalThis.removeEventListener("blur", onBlur);
      unsubscribe();
      marks.disconnect();
      removals.disconnect();
      host.dataset.state = "off";
    };
  }, []);

  return (
    <div
      ref={hostRef}
      data-state="off"
      data-inking="false"
      data-testid="drawlock-cursor-host"
      aria-hidden="true"
      className="drawlock-cursor"
      style={
        {
          "--drawlock-dot-size": `${DOT_SIZE}px`,
          "--drawlock-dot-scale": DOT_SCALE,
          "--drawlock-morph": `${MORPH_MS}ms`,
        } as CSSProperties
      }
    >
      <span className="drawlock-cursor__dot" />
    </div>
  );
}

export default DrawCursor;
